import {
  Show,
  For,
  createSignal,
  createMemo,
  createEffect,
  onCleanup,
  onMount,
  type Component,
  type JSX,
} from "solid-js";
import { PanelLayout, type SubTab } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { useConfirm, type ConfirmOptions } from "@/shared/confirm-dialog";
import { registerPanel } from "@/panel-registry";
import { CredentialInput, type CredentialInputResult } from "@/shared/credential-input";
import {
  type CompletionItem,
  type CompletionState,
  applyCompletion,
  cycleIndex,
  completionLabel,
  completionTypeMeta,
} from "./ps-completion";
import "./powershell.css";

// ── Types ──────────────────────────────────────────────────────────────────────

interface PsSession {
  psSessionId: string;
  name?: string;
  createdAt?: string;
  viaHelper?: boolean;
  context?: PsContext;
  runAs?: PowerShellRunAs;
  vmName?: string;
}

interface PsVariable {
  name: string;
  value: unknown;
  type?: string;
}

interface PsCommand {
  name: string;
  moduleName?: string;
  commandType?: string;
}

interface HistoryEntry {
  id: number;
  cmd: string;
  output: string;
  error?: boolean;
  cwd?: string;
}

type PsContext = "user" | "system" | "elevated" | "other" | "wts" | "vmdirect";

interface PowerShellRunAs {
  credentialRef: string;
  elevated: boolean;
  bindCredential: boolean;
  bindingName: string;
  bindingRef: string;
}

// État du menu de recherche inversée d'historique (Ctrl+R).
interface ReverseSearchState {
  query: string;
  index: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Déduplique en gardant la première occurrence (la plus récente). */
function dedupeRecent(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    if (seen.has(it)) continue;
    seen.add(it);
    out.push(it);
  }
  return out;
}

function isInternalPsCommand(raw: string): boolean {
  const cmd = raw.trim();
  return (
    cmd === "(Get-Location).Path" ||
    cmd.startsWith("$__rws_skip =") ||
    cmd.startsWith("$__rws_v = Get-Variable") ||
    cmd.startsWith("$ErrorActionPreference = 'Continue'\n$cmds = Get-Command") ||
    /^Get-Help\s+\S+\s+-Full\s+\|\s+Out-String\s+-Width\s+100$/i.test(cmd)
  );
}

function formatPsPrompt(cwd?: string | null): string {
  const path = (cwd || "").trim();
  return path ? `PS ${path}>` : "PS>";
}

/**
 * Filtre une liste de commandes (déjà triée du plus récent au plus ancien)
 * par sous-chaîne insensible à la casse. Query vide → toute la liste.
 */
function matchHistory(commands: string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter((c) => c.toLowerCase().includes(q));
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v.length > 120 ? v.slice(0, 120) + "…" : v;
  if (typeof v === "object") {
    try {
      const s = JSON.stringify(v);
      return s.length > 120 ? s.slice(0, 120) + "…" : s;
    } catch {
      return "[object]";
    }
  }
  return String(v);
}

function psContextLabel(context: PsContext | undefined): string {
  switch (context) {
    case "system":
      return "SYSTEM";
    case "elevated":
      return "Élevé";
    case "other":
      return "Autre user";
    case "wts":
      return "WTS";
    case "vmdirect":
      return "Direct VM";
    default:
      return "User";
  }
}

function shortCredentialRef(ref: string): string {
  const clean = ref.trim();
  if (!clean) return "";
  const parts = clean.split(":").filter(Boolean);
  return parts[parts.length - 1] || clean;
}

// ── Variables sub-tab ──────────────────────────────────────────────────────────

interface VariablesTabProps {
  sessionId: string;
  client: ReturnType<typeof useToolClient>;
}

const VariablesTab: Component<VariablesTabProps> = (props) => {
  const [rows, setRows] = createSignal<PsVariable[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [err, setErr] = createSignal("");
  const [filter, setFilter] = createSignal("");
  const [selected, setSelected] = createSignal<PsVariable | null>(null);
  let refreshSeq = 0;

  async function refresh(sessionId = props.sessionId) {
    const seq = ++refreshSeq;
    setLoading(true);
    setErr("");
    setSelected(null);
    try {
      const r = (await props.client.call("powershell_session_inspect_variables", {
        psSessionId: sessionId,
      })) as { variables?: PsVariable[] };
      if (seq === refreshSeq) setRows(r.variables || []);
    } catch (e: unknown) {
      if (seq === refreshSeq) setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (seq === refreshSeq) setLoading(false);
    }
  }

  createEffect(() => {
    void refresh(props.sessionId);
  });

  async function getDetail(name: string) {
    try {
      const r = (await props.client.call("powershell_session_get_variable", {
        psSessionId: props.sessionId,
        name,
      })) as { value?: unknown; type?: string };
      setSelected({ name, value: r.value, type: r.type });
    } catch {
      // ignore — grid row already shows preview
    }
  }

  const filtered = createMemo(() => {
    const f = filter().toLowerCase();
    if (!f) return rows();
    return rows().filter((r) => r.name.toLowerCase().includes(f));
  });

  const columns: Column<PsVariable>[] = [
    { accessorKey: "name", header: "Nom" },
    { accessorKey: "type", header: "Type", size: 100 },
    {
      id: "value",
      header: "Valeur",
      cell: (info) => formatValue(info.row.original.value),
    },
  ];

  return (
    <div class="ps-variables-tab">
      <div class="ps-variables-toolbar">
        <input
          class="panel-filter-input"
          placeholder="Filtrer les variables…"
          value={filter()}
          onInput={(e) => setFilter(e.currentTarget.value)}
        />
        <button class="btn btn--sm" onClick={() => refresh()} disabled={loading()}>
          {loading() ? "Chargement…" : "Actualiser"}
        </button>
      </div>

      <Show when={err()}>
        <div class="panel-error">{err()}</div>
      </Show>

      <div class="ps-variables-layout">
        <div class="ps-variables-grid">
          <DataTable
            rows={filtered()}
            columns={columns}
            emptyMessage={loading() ? "Chargement des variables..." : "Aucune variable."}
            rowActions={(row) => (
              <button class="btn btn--xs" onClick={() => getDetail(row.name)}>
                Détail
              </button>
            )}
          />
        </div>

        <Show when={selected()}>
          {(sel) => (
            <div class="ps-variable-detail">
              <div class="ps-variable-detail-header">
                <strong>${sel().name}</strong>
                <Show when={sel().type}>
                  <span class="ps-variable-type">[{sel().type}]</span>
                </Show>
                <button class="btn btn--xs btn--ghost" onClick={() => setSelected(null)}>
                  ✕
                </button>
              </div>
              <pre class="ps-variable-value">
                {typeof sel().value === "object" ? JSON.stringify(sel().value, null, 2) : String(sel().value ?? "")}
              </pre>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

// ── Commands sub-tab ───────────────────────────────────────────────────────────

interface CommandsTabProps {
  sessionId: string;
  client: ReturnType<typeof useToolClient>;
  onInsert: (cmd: string) => void;
}

const CommandsTab: Component<CommandsTabProps> = (props) => {
  const [rows, setRows] = createSignal<PsCommand[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [err, setErr] = createSignal("");
  const [pattern, setPattern] = createSignal("");

  async function search() {
    setLoading(true);
    setErr("");
    try {
      const r = (await props.client.call("powershell_session_get_commands", {
        psSessionId: props.sessionId,
        pattern: pattern() || "*",
        limit: 200,
      })) as { commands?: PsCommand[] };
      setRows(r.commands || []);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  const columns: Column<PsCommand>[] = [
    { accessorKey: "name", header: "Nom" },
    { accessorKey: "commandType", header: "Type", size: 90 },
    { accessorKey: "moduleName", header: "Module" },
  ];

  return (
    <div>
      <div class="ps-variables-toolbar">
        <input
          class="panel-filter-input"
          placeholder="Pattern (ex: Get-*, *Process*)"
          value={pattern()}
          onInput={(e) => setPattern(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <button class="btn btn--sm" onClick={search} disabled={loading()}>
          {loading() ? "Recherche…" : "Chercher"}
        </button>
      </div>

      <Show when={err()}>
        <div class="panel-error">{err()}</div>
      </Show>

      <DataTable
        rows={rows()}
        columns={columns}
        emptyMessage="Lancez une recherche."
        rowActions={(row) => (
          <button class="btn btn--xs" onClick={() => props.onInsert(row.name)}>
            ↙ Insérer
          </button>
        )}
      />
    </div>
  );
};

// ── Snippets sub-tab ───────────────────────────────────────────────────────────

interface Snippet {
  category: string;
  label: string;
  script: string;
}

const SNIPPETS: Snippet[] = [
  {
    category: "Réseau",
    label: "Connexions TCP actives",
    script: "Get-NetTCPConnection -State Established | Sort-Object RemoteAddress | Format-Table -AutoSize",
  },
  { category: "Réseau", label: "Vider le cache DNS", script: "Clear-DnsClientCache" },
  {
    category: "Réseau",
    label: "Lister les adresses IPv4",
    script: "Get-NetIPAddress -AddressFamily IPv4 | Format-Table InterfaceAlias, IPAddress -AutoSize",
  },
  {
    category: "Système",
    label: "Top 5 processus CPU",
    script: "Get-Process | Sort-Object CPU -Descending | Select-Object -First 5 Name, CPU, Id",
  },
  { category: "Système", label: "Redémarrer le spooler d'impression", script: "Restart-Service Spooler -Force" },
  {
    category: "Système",
    label: "Espace disque",
    script:
      "Get-PSDrive -PSProvider FileSystem | Select-Object Name, @{N='Used(GB)';E={[math]::Round($_.Used/1GB,1)}}, @{N='Free(GB)';E={[math]::Round($_.Free/1GB,1)}}",
  },
];

interface SnippetsTabProps {
  onInsert: (cmd: string) => void;
}

const SnippetsTab: Component<SnippetsTabProps> = (props) => {
  const [filter, setFilter] = createSignal("");
  const filtered = createMemo(() => {
    const f = filter().toLowerCase();
    if (!f) return SNIPPETS;
    return SNIPPETS.filter(
      (s) =>
        s.label.toLowerCase().includes(f) || s.category.toLowerCase().includes(f) || s.script.toLowerCase().includes(f),
    );
  });
  const categories = createMemo(() => [...new Set(filtered().map((s) => s.category))]);

  return (
    <div>
      <div class="ps-variables-toolbar">
        <input
          class="panel-filter-input"
          placeholder="Filtrer les snippets…"
          value={filter()}
          onInput={(e) => setFilter(e.currentTarget.value)}
        />
      </div>
      <Show when={filtered().length} fallback={<div class="panel-empty">Aucun snippet.</div>}>
        <For each={categories()}>
          {(cat) => (
            <div style={{ "margin-bottom": "12px" }}>
              <h4
                style={{
                  margin: "8px 0 4px",
                  "font-size": "11px",
                  "text-transform": "uppercase",
                  opacity: 0.6,
                }}
              >
                {cat}
              </h4>
              <For each={filtered().filter((s) => s.category === cat)}>
                {(s) => (
                  <div
                    style={{
                      display: "flex",
                      "align-items": "center",
                      gap: "8px",
                      padding: "4px 0",
                      "min-width": 0,
                    }}
                  >
                    <button
                      class="btn btn--xs"
                      onClick={() => props.onInsert(s.script)}
                      title="Insérer dans le terminal sans exécuter"
                    >
                      ↙ Insérer
                    </button>
                    <span style={{ "font-weight": 600, "font-size": "12px", "white-space": "nowrap" }}>{s.label}</span>
                    <code
                      style={{
                        "font-size": "11px",
                        opacity: 0.6,
                        overflow: "hidden",
                        "text-overflow": "ellipsis",
                        "white-space": "nowrap",
                      }}
                      title={s.script}
                    >
                      {s.script}
                    </code>
                  </div>
                )}
              </For>
            </div>
          )}
        </For>
      </Show>
    </div>
  );
};

// ── Terminal sub-tab ───────────────────────────────────────────────────────────

interface TerminalTabProps {
  sessionId: string;
  client: ReturnType<typeof useToolClient>;
  insertCmd: () => string;
  onInsertConsumed: () => void;
  context?: PsContext;
  runAs?: PowerShellRunAs;
  confirm: (opts?: ConfirmOptions) => Promise<boolean>;
}

interface HistoryFrame {
  seq: number;
  at: string;
  stream: "stdin" | "stdout" | "stderr";
  text: string;
  cwd?: string | null;
}

const TerminalTab: Component<TerminalTabProps> = (props) => {
  const [history, setHistory] = createSignal<HistoryEntry[]>([]);
  const [cmd, setCmd] = createSignal("");
  const [running, setRunning] = createSignal(false);
  const [currentCwd, setCurrentCwd] = createSignal("");
  const [_lastSeq, setLastSeq] = createSignal<number>(0);
  const [completion, setCompletion] = createSignal<CompletionState | null>(null);
  // Filtre du menu de complétion (champ de recherche "aller plus loin").
  const [compFilter, setCompFilter] = createSignal("");
  // Navigation historique avec flèches haut/bas (style shell).
  const [historyIndex, setHistoryIndex] = createSignal(-1);
  const [historyDraft, setHistoryDraft] = createSignal("");
  // Debounce de la complétion à la frappe.
  let compTimer: ReturnType<typeof setTimeout> | undefined;
  // Items affichés dans le menu : filtrés par compFilter, top-3 par défaut
  // (élargi à 8 quand on tape dans le champ de recherche pour aller plus loin).
  const compDisplayed = createMemo<CompletionItem[]>(() => {
    const c = completion();
    if (!c) return [];
    const f = compFilter().trim().toLowerCase();
    const base = f
      ? c.items.filter((it) => (completionLabel(it) + " " + it.completionText).toLowerCase().includes(f))
      : c.items;
    return base.slice(0, f ? 8 : 3);
  });
  // Popup de détail/manuel d'une complétion (brief = toolTip, manuel = Get-Help).
  const [helpView, setHelpView] = createSignal<{
    name: string;
    type: string;
    brief: string;
    manual: string;
    loading: boolean;
  } | null>(null);
  // Reverse search d'historique (Ctrl+R)
  const [rsearch, setRsearch] = createSignal<ReverseSearchState | null>(null);
  let historyRef: HTMLDivElement | undefined;
  let inputRef: HTMLInputElement | undefined;
  let rsearchRef: HTMLInputElement | undefined;
  let cwdRequestSeq = 0;
  const ws = useWs();

  // Liste des commandes saisies (du plus récent au plus ancien), dédupliquée.
  const commandHistory = createMemo(() =>
    dedupeRecent(
      history()
        .map((e) => e.cmd)
        .filter((c) => c.trim().length > 0 && !isInternalPsCommand(c)),
    ),
  );

  async function refreshCwd(sessionId = props.sessionId) {
    if ((props.context || "user") !== "user" || !sessionId) return;
    const seq = ++cwdRequestSeq;
    try {
      const r = (await props.client.call(
        "powershell_session_exec",
        { psSessionId: sessionId, script: "(Get-Location).Path", suppressHistory: true },
        { timeoutMs: 15_000 },
      )) as { stdout?: string; stderr?: string; cwd?: string };
      if (seq !== cwdRequestSeq || sessionId !== props.sessionId) return;
      const cwd = (r.cwd || r.stdout || "").trim();
      if (cwd) setCurrentCwd(cwd);
    } catch {
      if (seq === cwdRequestSeq) setCurrentCwd("");
    }
  }

  // ── Reverse search helpers ────────────────────────────────────────────────
  const rsearchMatches = createMemo(() => {
    const rs = rsearch();
    if (!rs) return [];
    return matchHistory(commandHistory(), rs.query);
  });

  function openReverseSearch() {
    if (running()) return;
    setCompletion(null);
    setRsearch({ query: "", index: 0 });
    setTimeout(() => rsearchRef?.focus(), 0);
  }

  function closeReverseSearch(insert: boolean) {
    const rs = rsearch();
    const matches = rsearchMatches();
    setRsearch(null);
    if (insert && rs && matches.length > 0) {
      const picked = matches[Math.min(rs.index, matches.length - 1)];
      setCmd(picked);
      setTimeout(() => {
        if (inputRef) {
          inputRef.setSelectionRange(picked.length, picked.length);
          inputRef.focus();
        }
      }, 0);
    } else {
      setTimeout(() => inputRef?.focus(), 0);
    }
  }

  function reverseSearchNext() {
    const matches = rsearchMatches();
    if (matches.length === 0) return;
    setRsearch((rs) => (rs ? { ...rs, index: cycleIndex(rs.index, matches.length, 1) } : rs));
  }

  // Replay du scrollback : uniquement pour les sessions User persistantes.
  createEffect(() => {
    if ((props.context || "user") !== "user") return;
    const sessionId = props.sessionId;
    setCurrentCwd("");
    void refreshCwd(sessionId);
    const replayRequestId = `replay_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const offReplay = ws.on(
      (m) => m.type === "ps:session:replay:result" && (m as { requestId?: string }).requestId === replayRequestId,
      // eslint-disable-next-line solid/reactivity -- handler ws.on, pas un scope réactif
      (m: unknown) => {
        const msg = m as {
          ok?: boolean;
          result?: { frames?: HistoryFrame[] };
          error?: string;
        };
        if (!msg.ok) return;
        const frames = msg.result?.frames || [];
        if (frames.length === 0) return;

        // Agrège frames en entries (1 stdin + ses stdout/stderr → 1 HistoryEntry)
        const entries: HistoryEntry[] = [];
        let current: HistoryEntry | null = null;
        let currentInternal = false;
        let maxSeq = 0;
        let replayCwd = "";
        for (const f of frames) {
          if (f.seq > maxSeq) maxSeq = f.seq;
          if (f.stream === "stdin") {
            if (current && !currentInternal) entries.push(current);
            currentInternal = isInternalPsCommand(f.text);
            const frameCwd = (f.cwd || replayCwd || currentCwd()).trim();
            if (frameCwd) replayCwd = frameCwd;
            current = currentInternal
              ? null
              : { id: entries.length, cmd: f.text, output: "", error: false, cwd: frameCwd || undefined };
          } else {
            if (currentInternal) continue;
            if (!current) current = { id: entries.length, cmd: "", output: "", error: false };
            current.output += f.text;
            if (f.stream === "stderr") current.error = true;
          }
        }
        if (current && !currentInternal) entries.push(current);
        setHistory(entries);
        setLastSeq(maxSeq);
        requestAnimationFrame(() => historyRef?.scrollTo(0, historyRef.scrollHeight));
      },
    );
    onCleanup(offReplay);
    ws.send({
      type: "ps:session:replay",
      requestId: replayRequestId,
      sessionId,
      sinceSeq: 0,
    });
  });

  // Watch for insert from Commands tab — reactive via createEffect
  createEffect(() => {
    const v = props.insertCmd();
    if (v) {
      setCmd(v);
      props.onInsertConsumed();
      inputRef?.focus();
    }
  });

  async function run() {
    const c = cmd().trim();
    if (!c || running()) return;
    setRunning(true);
    const id = history().length;
    const promptCwd = currentCwd();
    setHistory((h) => [...h, { id, cmd: c, output: "", error: false, cwd: promptCwd || undefined }]);
    setCmd("");
    setCompletion(null);
    setHistoryIndex(-1);
    setHistoryDraft("");
    try {
      let r: { stdout?: string; stderr?: string; cwd?: string };
      const ctx = props.context || "user";
      if (ctx === "system") {
        r = (await props.client.call("powershell_system_exec", { script: c }, { timeoutMs: 120_000 })) as {
          stdout?: string;
          stderr?: string;
        };
      } else if (ctx === "elevated") {
        r = (await props.client.call("powershell_elevated_exec", { script: c }, { timeoutMs: 120_000 })) as {
          stdout?: string;
          stderr?: string;
        };
      } else if (ctx === "other") {
        const runAs = props.runAs;
        const credentialRef = runAs?.credentialRef.trim() || "";
        if (!credentialRef) throw new Error("credentialRef requis.");

        const bindingName = runAs?.bindingName.trim() || "";
        const bindingRef = runAs?.bindingRef.trim() || credentialRef;
        const credentialBindings =
          runAs?.bindCredential && bindingName
            ? [
                {
                  name: bindingName,
                  credentialRef: bindingRef,
                },
              ]
            : undefined;

        const ok = await props.confirm({
          title: `PowerShell — ${shortCredentialRef(credentialRef)}`,
          body: runAs?.elevated ? "Exécution avec token haute intégrité." : "Exécution avec credentialRef stocké.",
          level: runAs?.elevated ? "danger" : "warn",
          confirmLabel: "Exécuter",
        });
        if (!ok) {
          setHistory((h) => h.map((e) => (e.id === id ? { ...e, output: "(annulé)", error: false } : e)));
          return;
        }

        r = (await props.client.call(
          "powershell_credential_exec",
          {
            script: c,
            credentialRef,
            elevated: !!runAs?.elevated,
            ...(credentialBindings ? { credentialBindings } : {}),
            timeoutMs: 120_000,
          },
          { timeoutMs: 130_000, userConfirmedAt: new Date().toISOString() },
        )) as { stdout?: string; stderr?: string };
      } else {
        r = (await props.client.call(
          "powershell_session_exec",
          { psSessionId: props.sessionId, script: c },
          { timeoutMs: 120_000 },
        )) as { stdout?: string; stderr?: string; cwd?: string };
        if (r.cwd) setCurrentCwd(r.cwd);
      }
      const output = r.stdout || r.stderr || "";
      setHistory((h) => h.map((e) => (e.id === id ? { ...e, output, error: !!r.stderr && !r.stdout } : e)));
    } catch (e: unknown) {
      setHistory((h) =>
        h.map((e2) => (e2.id === id ? { ...e2, output: e instanceof Error ? e.message : String(e), error: true } : e2)),
      );
    } finally {
      setRunning(false);
      requestAnimationFrame(() => historyRef?.scrollTo(0, historyRef.scrollHeight));
    }
  }

  async function handleCompletion(autoApply = true) {
    if ((props.context || "user") !== "user") return;
    const curIndex = inputRef?.selectionStart ?? cmd().length;
    try {
      const r = (await props.client.call("powershell_session_complete", {
        psSessionId: props.sessionId,
        input: cmd(),
        cursorIndex: curIndex,
      })) as {
        items?: CompletionItem[];
        replacementIndex?: number;
        replacementLength?: number;
      };

      const items = r.items || [];
      const replacementIndex = r.replacementIndex ?? 0;
      const replacementLength = r.replacementLength ?? 0;

      if (items.length === 0) {
        setCompletion(null);
      } else if (items.length === 1 && autoApply) {
        // Appliquer directement (uniquement sur Tab explicite, pas à la frappe)
        const result = applyCompletion(cmd(), replacementIndex, replacementLength, items[0].completionText);
        setCmd(result.text);
        setCompletion(null);
        setTimeout(() => {
          if (inputRef) {
            inputRef.setSelectionRange(result.cursor, result.cursor);
            inputRef.focus();
          }
        }, 0);
      } else {
        // Ouvrir le menu (top-3 + recherche)
        setCompFilter("");
        setCompletion({
          items,
          replacementIndex,
          replacementLength,
          selectedIndex: 0,
        });
      }
    } catch {
      // Silencieusement échouer
      setCompletion(null);
    }
  }

  function applyCompletionItem(item: CompletionItem) {
    const comp = completion();
    if (!comp) return;
    const result = applyCompletion(cmd(), comp.replacementIndex, comp.replacementLength, item.completionText);
    setCmd(result.text);
    setCompletion(null);
    setCompFilter("");
    setTimeout(() => {
      if (inputRef) {
        inputRef.setSelectionRange(result.cursor, result.cursor);
        inputRef.focus();
      }
    }, 0);
  }

  function applySelectedCompletion() {
    const disp = compDisplayed();
    const comp = completion();
    if (!comp || comp.selectedIndex < 0 || comp.selectedIndex >= disp.length) return;
    applyCompletionItem(disp[comp.selectedIndex]);
  }

  // Affiche le détail d'une complétion : explication brève (toolTip) immédiate,
  // puis le manuel complet (Get-Help) chargé en async pour les commandes.
  async function showItemHelp(item: CompletionItem, e?: MouseEvent) {
    e?.preventDefault();
    e?.stopPropagation();
    const name = item.completionText;
    const isCmd = item.resultType === "Command";
    const canFetch = isCmd && (props.context || "user") === "user" && !!props.sessionId;
    setHelpView({ name, type: item.resultType, brief: item.toolTip || "", manual: "", loading: canFetch });
    if (!canFetch) return;
    try {
      const r = (await props.client.call(
        "powershell_session_exec",
        {
          psSessionId: props.sessionId,
          script: `Get-Help ${name} -Full | Out-String -Width 100`,
          suppressHistory: true,
        },
        { timeoutMs: 30_000 },
      )) as { stdout?: string; stderr?: string };
      setHelpView((h) =>
        h && h.name === name ? { ...h, manual: (r.stdout || r.stderr || "(aucun manuel)").trim(), loading: false } : h,
      );
    } catch (err) {
      setHelpView((h) =>
        h && h.name === name ? { ...h, manual: err instanceof Error ? err.message : String(err), loading: false } : h,
      );
    }
  }

  return (
    <div class="ps-terminal">
      <div class="ps-terminal-history" ref={historyRef}>
        <For each={history()}>
          {(entry) => (
            <div class="ps-terminal-entry">
              <div class="ps-terminal-cmd">
                {formatPsPrompt(entry.cwd ?? currentCwd())} {entry.cmd}
              </div>
              <Show when={entry.output.length > 0}>
                <pre class="ps-terminal-output" data-error={entry.error || undefined}>
                  {entry.output}
                </pre>
              </Show>
            </div>
          )}
        </For>
        <Show when={running()}>
          <div class="ps-terminal-running">…</div>
        </Show>
      </div>
      <div class="ps-terminal-input-row" style={{ position: "relative" }}>
        <span class="ps-terminal-prompt" title={formatPsPrompt(currentCwd())}>
          {formatPsPrompt(currentCwd())}
        </span>
        <input
          ref={inputRef}
          class="ps-terminal-input"
          value={cmd()}
          onInput={(e) => {
            const val = e.currentTarget.value;
            setCmd(val);
            setCompFilter("");
            setHistoryIndex(-1);
            setHistoryDraft("");
            if (compTimer) clearTimeout(compTimer);
            // Complétion à la frappe (debounce) : token significatif, pas en fin d'espace,
            // sessions User uniquement (le backend complete n'existe que là).
            if ((props.context || "user") === "user" && val.trim().length >= 2 && !/\s$/.test(val) && !running()) {
              compTimer = setTimeout(() => handleCompletion(false), 250);
            } else {
              setCompletion(null);
            }
          }}
          onKeyDown={(e) => {
            const comp = completion();
            if ((e.key === "r" || e.key === "R") && e.ctrlKey) {
              e.preventDefault();
              openReverseSearch();
              return;
            }
            if (e.key === "Tab") {
              e.preventDefault();
              if (e.shiftKey) {
                if (comp) {
                  setCompletion((c) =>
                    c ? { ...c, selectedIndex: cycleIndex(c.selectedIndex, compDisplayed().length, -1) } : null,
                  );
                }
              } else {
                if (!comp) {
                  handleCompletion();
                } else {
                  setCompletion((c) =>
                    c ? { ...c, selectedIndex: cycleIndex(c.selectedIndex, compDisplayed().length, 1) } : null,
                  );
                }
              }
            } else if (e.key === "ArrowDown" && comp) {
              e.preventDefault();
              setCompletion((c) =>
                c ? { ...c, selectedIndex: cycleIndex(c.selectedIndex, compDisplayed().length, 1) } : null,
              );
            } else if (e.key === "ArrowUp" && comp) {
              e.preventDefault();
              setCompletion((c) =>
                c ? { ...c, selectedIndex: cycleIndex(c.selectedIndex, compDisplayed().length, -1) } : null,
              );
            } else if (e.key === "ArrowUp" && !comp) {
              e.preventDefault();
              const hist = commandHistory();
              if (hist.length === 0) return;
              let nextText: string;
              if (historyIndex() === -1) {
                setHistoryDraft(cmd());
                setHistoryIndex(0);
                nextText = hist[0];
                setCmd(nextText);
              } else if (historyIndex() < hist.length - 1) {
                const next = historyIndex() + 1;
                setHistoryIndex(next);
                nextText = hist[next];
                setCmd(nextText);
              } else {
                nextText = cmd();
              }
              setTimeout(() => {
                if (inputRef) inputRef.setSelectionRange(nextText.length, nextText.length);
              }, 0);
            } else if (e.key === "ArrowDown" && !comp) {
              e.preventDefault();
              const hist = commandHistory();
              if (historyIndex() === -1) return;
              let nextText: string;
              if (historyIndex() > 0) {
                const next = historyIndex() - 1;
                setHistoryIndex(next);
                nextText = hist[next];
                setCmd(nextText);
              } else {
                setHistoryIndex(-1);
                nextText = historyDraft();
                setCmd(nextText);
                setHistoryDraft("");
              }
              setTimeout(() => {
                if (inputRef) inputRef.setSelectionRange(nextText.length, nextText.length);
              }, 0);
            } else if (e.key === "Enter") {
              if (comp) {
                e.preventDefault();
                applySelectedCompletion();
              } else {
                run();
              }
            } else if (e.key === "Escape" && comp) {
              e.preventDefault();
              setCompletion(null);
            }
          }}
          disabled={running()}
          placeholder="Commande PowerShell…"
          autocomplete="off"
          spellcheck={false}
        />
        <button class="btn btn--sm" onClick={run} disabled={running() || !cmd().trim()}>
          ▶
        </button>

        <Show when={completion()}>
          {(comp) => (
            <div class="ps-completion-menu">
              <For each={compDisplayed()}>
                {(item, idx) => (
                  <div
                    class="ps-completion-item"
                    data-active={idx() === comp().selectedIndex || undefined}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyCompletionItem(item);
                    }}
                    onMouseEnter={() => setCompletion((c) => (c ? { ...c, selectedIndex: idx() } : c))}
                    title={item.toolTip || undefined}
                  >
                    <span class="ps-completion-icon" data-type={item.resultType} aria-hidden="true">
                      {completionTypeMeta(item.resultType).icon}
                    </span>
                    <span class="ps-completion-item-text">{completionLabel(item)}</span>
                    <span class="ps-completion-type">{completionTypeMeta(item.resultType).label}</span>
                    <button
                      class="ps-completion-help"
                      title="Détail / manuel (Get-Help)"
                      aria-label="Détail"
                      onMouseDown={(e) => showItemHelp(item, e)}
                    >
                      ⓘ
                    </button>
                  </div>
                )}
              </For>
              <Show when={compDisplayed().length === 0}>
                <div class="ps-completion-empty">aucune correspondance</div>
              </Show>
              <div class="ps-completion-footer">
                <input
                  class="ps-completion-search"
                  value={compFilter()}
                  placeholder="filtrer…"
                  autocomplete="off"
                  spellcheck={false}
                  onInput={(e) => {
                    setCompFilter(e.currentTarget.value);
                    setCompletion((c) => (c ? { ...c, selectedIndex: 0 } : c));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applySelectedCompletion();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setCompletion(null);
                      inputRef?.focus();
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setCompletion((c) =>
                        c ? { ...c, selectedIndex: cycleIndex(c.selectedIndex, compDisplayed().length, 1) } : c,
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setCompletion((c) =>
                        c ? { ...c, selectedIndex: cycleIndex(c.selectedIndex, compDisplayed().length, -1) } : c,
                      );
                    }
                  }}
                />
                <span class="ps-completion-count">
                  {compDisplayed().length}/{comp().items.length}
                </span>
              </div>
            </div>
          )}
        </Show>

        <Show when={helpView()}>
          {(h) => (
            <div class="ps-help-popup">
              <div class="ps-help-popup-header">
                <span class="ps-help-popup-icon" aria-hidden="true">
                  {completionTypeMeta(h().type).icon}
                </span>
                <span class="ps-help-popup-name">{h().name}</span>
                <span class="ps-help-popup-type">{completionTypeMeta(h().type).label}</span>
                <button
                  class="btn btn--xs btn--ghost"
                  title="Fermer"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setHelpView(null);
                  }}
                >
                  ✕
                </button>
              </div>
              <Show when={h().brief}>
                <div class="ps-help-popup-brief">{h().brief}</div>
              </Show>
              <Show when={h().loading}>
                <div class="ps-help-popup-loading">Chargement du manuel (Get-Help)…</div>
              </Show>
              <Show when={h().manual}>
                <pre class="ps-help-popup-manual">{h().manual}</pre>
              </Show>
              <Show when={!h().loading && !h().manual && !h().brief}>
                <div class="ps-help-popup-loading">(pas de détail disponible pour ce type)</div>
              </Show>
            </div>
          )}
        </Show>

        <Show when={rsearch()}>
          {(rs) => (
            <div class="ps-rsearch">
              <span class="ps-rsearch-label">(reverse-i-search)</span>
              <input
                ref={rsearchRef}
                class="ps-rsearch-input"
                value={rs().query}
                placeholder="rechercher dans l'historique…"
                autocomplete="off"
                spellcheck={false}
                onInput={(e) => {
                  const q = e.currentTarget.value;
                  setRsearch((cur) => (cur ? { ...cur, query: q, index: 0 } : cur));
                }}
                onKeyDown={(e) => {
                  if ((e.key === "r" || e.key === "R") && e.ctrlKey) {
                    e.preventDefault();
                    reverseSearchNext();
                  } else if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    closeReverseSearch(true);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    closeReverseSearch(false);
                  } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    reverseSearchNext();
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    const matches = rsearchMatches();
                    if (matches.length > 0) {
                      setRsearch((cur) => (cur ? { ...cur, index: cycleIndex(cur.index, matches.length, -1) } : cur));
                    }
                  }
                }}
              />
              <span class="ps-rsearch-match">
                <Show
                  when={rsearchMatches().length > 0}
                  fallback={<span class="ps-rsearch-empty">aucun résultat</span>}
                >
                  <code>{rsearchMatches()[Math.min(rs().index, rsearchMatches().length - 1)]}</code>
                  <span class="ps-rsearch-count">
                    {Math.min(rs().index + 1, rsearchMatches().length)}/{rsearchMatches().length}
                  </span>
                </Show>
              </span>
              <button class="btn btn--xs btn--ghost" onClick={() => closeReverseSearch(false)} title="Annuler (Échap)">
                ✕
              </button>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

// ── Split view (Terminal + Variables empilés) ───────────────────────────────────

interface SplitViewProps {
  topId: string;
  client: ReturnType<typeof useToolClient>;
  insertCmd: () => string;
  onInsertConsumed: () => void;
  context?: PsContext;
  runAs?: PowerShellRunAs;
  confirm: (opts?: ConfirmOptions) => Promise<boolean>;
}

const SplitView: Component<SplitViewProps> = (props) => {
  // Hauteur du volet haut (Terminal) en pourcentage du conteneur.
  const [topPct, setTopPct] = createSignal(60);
  let containerRef: HTMLDivElement | undefined;
  let dragging = false;

  function onPointerDown(e: PointerEvent) {
    dragging = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragging || !containerRef) return;
    const rect = containerRef.getBoundingClientRect();
    if (rect.height <= 0) return;
    const pct = ((e.clientY - rect.top) / rect.height) * 100;
    setTopPct(Math.max(20, Math.min(80, pct)));
  }

  function onPointerUp(e: PointerEvent) {
    if (!dragging) return;
    dragging = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore — pointer déjà relâché
    }
  }

  return (
    <div class="ps-split" ref={containerRef}>
      <div class="ps-split-pane" style={{ height: `${topPct()}%` }}>
        <TerminalTab
          sessionId={props.topId}
          client={props.client}
          insertCmd={props.insertCmd}
          onInsertConsumed={props.onInsertConsumed}
          context={props.context}
          runAs={props.runAs}
          confirm={props.confirm}
        />
      </div>
      <div
        class="ps-split-resizer"
        role="separator"
        aria-orientation="horizontal"
        title="Glisser pour redimensionner"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span class="ps-split-resizer-grip" aria-hidden="true" />
      </div>
      <div class="ps-split-pane" style={{ height: `${100 - topPct()}%` }}>
        <VariablesTab sessionId={props.topId} client={props.client} />
      </div>
    </div>
  );
};

// ── Session selector ───────────────────────────────────────────────────────────

interface SessionSelectorProps {
  sessions: PsSession[];
  activeId: string;
  onSelect: (id: string) => void;
  onNew: () => void;
  onClose: (id: string) => void;
  opening: boolean;
}

const SessionSelector: Component<SessionSelectorProps> = (props) => (
  <div class="ps-session-selector">
    <For each={props.sessions}>
      {(s) => (
        <div
          class="ps-session-chip"
          data-active={props.activeId === s.psSessionId || undefined}
          onClick={() => props.onSelect(s.psSessionId)}
        >
          <span class="ps-session-chip-label">{s.name || s.psSessionId.slice(0, 8)}</span>
          <Show when={s.viaHelper}>
            <span class="ps-session-chip-badge">helper</span>
          </Show>
          <Show when={s.context && s.context !== "user"}>
            <span class="ps-session-chip-badge">{psContextLabel(s.context)}</span>
          </Show>
          <Show when={s.context === "vmdirect" && s.vmName}>
            <span class="ps-session-chip-badge ps-session-chip-badge--vm" title="VM cible PowerShell Direct">
              {s.vmName}
            </span>
          </Show>
          <button
            class="ps-session-chip-close"
            onClick={(e) => {
              e.stopPropagation();
              props.onClose(s.psSessionId);
            }}
          >
            ✕
          </button>
        </div>
      )}
    </For>
    <button
      class="btn btn--xs"
      onClick={() => {
        console.log("[ps] SessionSelector onNew");
        props.onNew();
      }}
      disabled={props.opening}
    >
      {props.opening ? "Ouverture…" : "+ Session"}
    </button>
  </div>
);

// ── Main panel ────────────────────────────────────────────────────────────────

const PowerShellPanel: Component<{ vaultDefaults?: Record<string, string> }> = (props) => {
  const ws = useWs();
  const client = useToolClient();
  const confirm = useConfirm();

  const [sessions, setSessions] = createSignal<PsSession[]>([]);
  const [activeId, setActiveId] = createSignal<string>("");
  const [opening, setOpening] = createSignal(false);
  const [globalErr, setGlobalErr] = createSignal("");
  const [pendingInsert, setPendingInsert] = createSignal("");
  const [splitMode, setSplitMode] = createSignal(false);
  const [newContext, setNewContext] = createSignal<PsContext>("user");
  const [runasResult, setRunasResult] = createSignal<CredentialInputResult>({ mode: "manual", ready: false });
  const [wtsUsername, setWtsUsername] = createSignal("");
  const [runAsElevated, setRunAsElevated] = createSignal(false);
  const [runAsBindCredential, setRunAsBindCredential] = createSignal(true);
  const [runAsBindingName, setRunAsBindingName] = createSignal("vaultCred");
  const [runAsBindingRef, setRunAsBindingRef] = createSignal("");
  const [showSessionPicker, setShowSessionPicker] = createSignal(false);
  // ── Contexte vmdirect (PowerShell Direct vers une VM Hyper-V) ──────────────
  // Liste des VMs chargée via hyperv_vms_list (réutilise le plugin HyperV).
  // On calque l'interface HyperVm du panel hyperv (champs PascalCase, State en
  // texte : "Running" / "Off" / "Saved"…).
  interface HvVm {
    Name?: string;
    State?: string;
  }
  const [hvVms, setHvVms] = createSignal<HvVm[]>([]);
  const [hvVmsLoading, setHvVmsLoading] = createSignal(false);
  const [hvVmsError, setHvVmsError] = createSignal("");
  const [vmdirectVm, setVmdirectVm] = createSignal("");
  const [vmdirectResult, setVmdirectResult] = createSignal<CredentialInputResult>({
    mode: "manual",
    ready: false,
  });

  // PowerShell Direct n'a de sens que sur un hôte Hyper-V : on gate l'option
  // derrière la feature `hyperv` annoncée par l'agent (BuildAgentFeatures).
  const canVmDirect = createMemo(() => !!ws.state.agentFeatures?.hyperv);

  // Les VMs en état « Running » sont seules éligibles à PowerShell Direct
  // (la VM doit être allumée et avoir Integration Services opérationnels).
  const runningVms = createMemo(() => hvVms().filter((v) => String(v.State ?? "").toLowerCase() === "running"));
  const nonRunningVms = createMemo(() => hvVms().filter((v) => String(v.State ?? "").toLowerCase() !== "running"));

  function activeSessionStorageKey() {
    const sessionCode = ws.state.sessionCode || new URLSearchParams(location.search).get("s") || "global";
    return `rws:powershell:active:${sessionCode}`;
  }

  function readStoredActiveId() {
    try {
      return sessionStorage.getItem(activeSessionStorageKey()) || "";
    } catch {
      return "";
    }
  }

  function persistActiveId(id: string) {
    try {
      if (id) sessionStorage.setItem(activeSessionStorageKey(), id);
      else sessionStorage.removeItem(activeSessionStorageKey());
    } catch {
      // ignore storage errors
    }
  }

  function newestSessionId(list: PsSession[]) {
    let best = list[0];
    let bestTs = best?.createdAt ? Date.parse(best.createdAt) : Number.NaN;
    for (const item of list.slice(1)) {
      const ts = item.createdAt ? Date.parse(item.createdAt) : Number.NaN;
      if (!best || (Number.isFinite(ts) && (!Number.isFinite(bestTs) || ts > bestTs))) {
        best = item;
        bestTs = ts;
      }
    }
    return best?.psSessionId || "";
  }

  function chooseInitialActiveId(list: PsSession[]) {
    const stored = readStoredActiveId();
    if (stored && list.some((s) => s.psSessionId === stored)) return stored;
    return newestSessionId(list);
  }

  function selectActiveSession(id: string) {
    setActiveId(id);
    persistActiveId(id);
  }

  const activeSession = createMemo(() => sessions().find((s) => s.psSessionId === activeId()));
  const activeContext = createMemo(() => activeSession()?.context || "user");
  const activeRunAs = createMemo(() => activeSession()?.runAs);

  createEffect(() => {
    if (activeContext() !== "user" && splitMode()) setSplitMode(false);
  });

  // Garde-fou : si l'hôte perd la feature Hyper-V (ou n'en a jamais eu), on
  // force le sélecteur de contexte hors « vmdirect » pour ne jamais laisser
  // l'interface dans un état où la toolbar/option serait orpheline. Le
  // fonctionnement PowerShell normal (user/system/elevated/other/wts) reste
  // strictement inchangé sur les hôtes sans Hyper-V.
  createEffect(() => {
    if (!canVmDirect() && newContext() === "vmdirect") setNewContext("user");
  });

  // ── Timeline des actions des autres opérateurs ────────────────────────────
  interface PeerAction {
    peerId: string;
    action: string;
    detail: string;
    ts: string;
  }
  const [peerActions, setPeerActions] = createSignal<PeerAction[]>([]);
  function addPeerAction(ev: { peerId?: string; action?: string; detail?: string; ts?: string }) {
    if (!ev.peerId || ev.peerId === ws.state.myPeerId) return;
    setPeerActions((prev) => {
      const next = [
        { peerId: ev.peerId!, action: ev.action || "", detail: ev.detail || "", ts: ev.ts || new Date().toISOString() },
        ...prev,
      ].slice(0, 50);
      return next;
    });
  }
  // Charger les sessions existantes au mount (récupère aussi les sessions Helper)
  onMount(async () => {
    try {
      const r = (await client.call("powershell_session_list", {})) as { sessions?: PsSession[] };
      if (r.sessions && r.sessions.length > 0) {
        setSessions(r.sessions);
        selectActiveSession(chooseInitialActiveId(r.sessions));
      }
    } catch {
      // ignore — pas de sessions existantes
    }
    // Pré-charge la liste des VMs Hyper-V (silencieux si l'hôte n'est pas Hyper-V).
    void refreshHvVms();
  });

  async function refreshHvVms() {
    if (!canVmDirect()) return;
    setHvVmsLoading(true);
    setHvVmsError("");
    try {
      // hyperv_vms_list est exposé par le plugin HyperV (WMI Msvm_ComputerSystem).
      // Le retour normalise rows/Rows (PascalCase, comme le panel hyperv).
      const r = (await client.call("hyperv_vms_list", {}, { timeoutMs: 30_000 })) as {
        rows?: HvVm[];
        Rows?: HvVm[];
      };
      const rows = r.rows ?? r.Rows ?? [];
      // On ne garde que les VMs nommées (les entries sans Name sont des artefacts).
      setHvVms(rows.filter((v) => v.Name).sort((a, b) => String(a.Name).localeCompare(String(b.Name))));
    } catch (e: unknown) {
      setHvVmsError(e instanceof Error ? e.message : String(e));
      setHvVms([]);
    } finally {
      setHvVmsLoading(false);
    }
  }

  async function openSession() {
    const ctx = newContext();
    const runasCred = runasResult();
    const vmdirectCred = vmdirectResult();
    const vmName = ctx === "vmdirect" ? vmdirectVm().trim() : "";
    const credentialRef =
      ctx === "other"
        ? runasCred.mode === "volt" && runasCred.credentialId
          ? runasCred.credentialId
          : ""
        : ctx === "wts"
          ? wtsUsername().trim() || undefined
          : ctx === "vmdirect"
            ? vmdirectCred.mode === "volt" && vmdirectCred.credentialId
              ? vmdirectCred.credentialId
              : ""
            : undefined;
    setOpening(true);
    setGlobalErr("");
    try {
      // Réutilise une session existante avec le même contexte + credentialRef
      // (+ vmName pour vmdirect : deux sessions vers deux VMs coexistent).
      const existing = sessions().find(
        (s) =>
          s.context === ctx &&
          (ctx !== "other" || s.runAs?.credentialRef === credentialRef) &&
          (ctx !== "vmdirect" || s.vmName === vmName),
      );
      if (existing) {
        selectActiveSession(existing.psSessionId);
        return;
      }

      if (ctx === "other" && !credentialRef) {
        setGlobalErr("credentialRef requis.");
        return;
      }
      if (ctx === "vmdirect") {
        if (!vmName) {
          setGlobalErr("Sélectionnez une VM cible.");
          return;
        }
        if (!credentialRef) {
          setGlobalErr("credentialRef invité requis pour PowerShell Direct.");
          return;
        }
      }

      const req: Record<string, unknown> = { context: ctx };
      if (credentialRef) req.credentialRef = credentialRef;
      if (ctx === "vmdirect") req.vmName = vmName;
      const r = (await client.call("powershell_session_open", req, {
        timeoutMs: ctx === "vmdirect" ? 60_000 : undefined,
      })) as PsSession;
      const runAs: PowerShellRunAs | undefined =
        ctx === "other"
          ? {
              credentialRef: credentialRef!,
              elevated: runAsElevated(),
              bindCredential: runAsBindCredential(),
              bindingName: runAsBindingName().trim() || "vaultCred",
              bindingRef: runAsBindingRef().trim(),
            }
          : undefined;
      const name =
        ctx === "other"
          ? `${runAs!.elevated ? "RUNAS+" : "RUNAS"} ${shortCredentialRef(credentialRef!)}`
          : ctx === "wts" && credentialRef
            ? `WTS ${shortCredentialRef(credentialRef)}`
            : ctx === "vmdirect"
              ? `VM ${vmName}`
              : ctx.toUpperCase();
      setSessions((prev) => [...prev, { ...r, name, context: ctx, runAs, vmName }]);
      selectActiveSession(r.psSessionId);
    } catch (e: unknown) {
      setGlobalErr(e instanceof Error ? e.message : String(e));
    } finally {
      setOpening(false);
    }
  }

  async function closeSession(id: string) {
    try {
      await client.call("powershell_session_close", { psSessionId: id });
    } catch {
      // ignore
    }
    const remaining = sessions().filter((s) => s.psSessionId !== id);
    setSessions(remaining);
    if (activeId() === id || !remaining.some((s) => s.psSessionId === activeId())) {
      selectActiveSession(newestSessionId(remaining));
    }
  }

  // Track insert requests from Commands tab
  function handleInsert(cmd: string) {
    setPendingInsert(cmd);
  }
  function insertCmd() {
    return pendingInsert();
  }
  function onInsertConsumed() {
    setPendingInsert("");
  }

  function pickOrOpenSession() {
    console.log("[ps] pickOrOpenSession sessions.length=", sessions().length);
    if (sessions().length === 0) {
      void openSession();
      return;
    }
    setShowSessionPicker(true);
  }

  function selectExistingSession(id: string) {
    selectActiveSession(id);
    setShowSessionPicker(false);
  }

  // Cleanup WS listener on unmount
  const offPs = ws.on(
    (m) => m.type === "ps:session_closed",
    // eslint-disable-next-line solid/reactivity -- handler ws.on, pas un scope réactif
    (m: unknown) => {
      const msg = m as { psSessionId?: string };
      if (msg.psSessionId) {
        const remaining = sessions().filter((s) => s.psSessionId !== msg.psSessionId);
        setSessions(remaining);
        if (activeId() === msg.psSessionId || !remaining.some((s) => s.psSessionId === activeId())) {
          selectActiveSession(newestSessionId(remaining));
        }
      }
    },
  );
  const offAudit = ws.on(
    (m) => m.type === "audit:event",
    (m: unknown) => {
      const msg = m as { event?: { action?: string; detail?: string; peerId?: string; ts?: string } };
      const ev = msg.event;
      if (!ev) return;
      if (ev.action === "tool_requested" && ev.detail?.startsWith("powershell_")) {
        addPeerAction(ev);
      }
    },
  );
  onCleanup(() => {
    offPs();
    offAudit();
  });

  // Sub-tabs for the active session — désactivés en mode "Vue partagée"
  // (PanelLayout rend `children` quand subtabs est vide).
  const subtabs = createMemo((): SubTab[] => {
    const id = activeId();
    if (!id || splitMode()) return [];
    return [
      {
        id: "terminal",
        label: "Terminal",
        render: () => (
          <TerminalTab
            sessionId={id}
            client={client}
            insertCmd={insertCmd}
            onInsertConsumed={onInsertConsumed}
            context={activeContext()}
            runAs={activeRunAs()}
            confirm={confirm.confirm}
          />
        ),
      },
      ...(activeContext() === "user"
        ? [
            {
              id: "variables",
              label: "Variables",
              render: () => <VariablesTab sessionId={id} client={client} />,
            },
            {
              id: "commands",
              label: "Commandes",
              render: () => <CommandsTab sessionId={id} client={client} onInsert={handleInsert} />,
            },
          ]
        : []),
      {
        id: "snippets",
        label: "Snippets",
        render: () => <SnippetsTab onInsert={handleInsert} />,
      },
    ];
  });

  const toolbar = (): JSX.Element => (
    <>
      <SessionSelector
        sessions={sessions()}
        activeId={activeId()}
        onSelect={selectActiveSession}
        onNew={pickOrOpenSession}
        onClose={closeSession}
        opening={opening()}
      />
      <select
        class="btn btn--xs"
        value={newContext()}
        onChange={(e) => setNewContext(e.currentTarget.value as PsContext)}
        title="Contexte d'exécution"
      >
        <option value="user">User</option>
        <option value="system">SYSTEM</option>
        <option value="elevated">Élevé</option>
        <option value="other">Autre user</option>
        <option value="wts">WTS</option>
        <Show when={canVmDirect()}>
          <option value="vmdirect">Direct (VM)</option>
        </Show>
      </select>
      <Show when={newContext() === "other"}>
        <div class="ps-runas-toolbar">
          <CredentialInput
            usage="powershell-runas"
            vaultDefaults={props.vaultDefaults}
            layout="inline"
            fields={{ username: false, password: false, domain: false }}
            voltLabel="🔑 VOLT"
            disabled={opening()}
            // eslint-disable-next-line solid/reactivity -- stub async, credentialRef résolu côté agent
            requestAgentKey={async () => {
              // Le scellement VOLT échoue silencieusement si l'agent n'est pas
              // prêt ; seul credentialId est consommé (résolu côté agent via
              // ProxyCredentialVault). On fournit un stub qui throw si appelé.
              throw new Error("requestAgentKey non requis pour powershell-runas (credentialRef résolu côté agent)");
            }}
            onResult={(r) => setRunasResult(r)}
          />
          <label class="ps-runas-check" title="Utiliser le token UAC haute intégrité si disponible">
            <input
              type="checkbox"
              checked={runAsElevated()}
              onChange={(e) => setRunAsElevated(e.currentTarget.checked)}
            />
            Élevé
          </label>
          <label class="ps-runas-check" title="Créer une variable PSCredential dans le script">
            <input
              type="checkbox"
              checked={runAsBindCredential()}
              onChange={(e) => setRunAsBindCredential(e.currentTarget.checked)}
            />
            $
          </label>
          <input
            class="ps-runas-var"
            value={runAsBindingName()}
            onInput={(e) => setRunAsBindingName(e.currentTarget.value)}
            disabled={!runAsBindCredential()}
            placeholder="vaultCred"
            autocomplete="off"
            spellcheck={false}
            title="Nom de variable PSCredential injectée"
          />
          <input
            class="ps-runas-input"
            value={runAsBindingRef()}
            onInput={(e) => setRunAsBindingRef(e.currentTarget.value)}
            disabled={!runAsBindCredential()}
            placeholder="binding ref"
            autocomplete="off"
            spellcheck={false}
            title="credentialRef injecté en variable, vide = même ref"
          />
        </div>
      </Show>
      <Show when={newContext() === "wts"}>
        <div class="ps-runas-toolbar">
          <input
            class="ps-runas-input"
            value={wtsUsername()}
            onInput={(e) => setWtsUsername(e.currentTarget.value)}
            placeholder="utilisateur WTS (optionnel)"
            autocomplete="off"
            spellcheck={false}
            title="Nom d'utilisateur de la session Windows active (optionnel)"
          />
        </div>
      </Show>
      <Show when={newContext() === "vmdirect"}>
        <div class="ps-vmdirect-toolbar">
          <select
            class="ps-vmdirect-vm-select"
            value={vmdirectVm()}
            onChange={(e) => setVmdirectVm(e.currentTarget.value)}
            disabled={opening() || hvVmsLoading()}
            title="VM Hyper-V cible (doit être allumée)"
          >
            <option value="">{hvVmsLoading() ? "Chargement des VMs…" : "— Sélectionner une VM —"}</option>
            <Show when={runningVms().length > 0}>
              <optgroup label="Allumées (éligibles)">
                <For each={runningVms()}>{(vm) => <option value={vm.Name!}>{vm.Name}</option>}</For>
              </optgroup>
            </Show>
            <Show when={nonRunningVms().length > 0}>
              <optgroup label="Éteintes / Suspendues">
                <For each={nonRunningVms()}>
                  {(vm) => (
                    <option value={vm.Name!} disabled>
                      {vm.Name} ({vm.State || "?"})
                    </option>
                  )}
                </For>
              </optgroup>
            </Show>
          </select>
          <button
            class="btn btn--xs"
            onClick={() => void refreshHvVms()}
            disabled={opening() || hvVmsLoading()}
            title="Rafraîchir la liste des VMs"
          >
            ⟳
          </button>
          <CredentialInput
            usage="powershell-vmdirect"
            vaultDefaults={props.vaultDefaults}
            layout="inline"
            fields={{ username: false, password: false, domain: false }}
            voltLabel="🔑 VOLT"
            disabled={opening()}
            // eslint-disable-next-line solid/reactivity -- stub async, credentialRef résolu côté agent
            requestAgentKey={async () => {
              throw new Error("requestAgentKey non requis pour powershell-vmdirect (credentialRef résolu côté agent)");
            }}
            onResult={(r) => setVmdirectResult(r)}
          />
        </div>
        <Show when={hvVmsError()}>
          <span class="ps-vmdirect-error" title={hvVmsError()}>
            ⚠ {hvVmsError().slice(0, 40)}
          </span>
        </Show>
      </Show>
      <Show when={activeId()}>
        <button
          class="btn btn--xs"
          classList={{ "btn--primary": splitMode() }}
          disabled={activeContext() !== "user"}
          onClick={() => setSplitMode((v) => !v)}
          title="Afficher le Terminal et les Variables simultanément"
        >
          {splitMode() ? "▭ Vue onglets" : "⬓ Vue partagée"}
        </button>
      </Show>
    </>
  );

  return (
    <PanelLayout
      title="PowerShell"
      toolbar={toolbar()}
      subtabs={subtabs()}
      status={
        globalErr()
          ? { text: globalErr(), level: "danger" }
          : sessions().length === 0
            ? { text: "Aucune session ouverte", level: "warn" }
            : { text: `${sessions().length} session(s) active(s)` }
      }
    >
      <Show when={peerActions().length > 0}>
        <div class="ps-peer-actions">
          <For each={peerActions().slice(0, 3)}>
            {(a) => (
              <div class="ps-peer-action">
                <span class="ps-peer-id">{a.peerId.slice(0, 6)}…</span>
                <span class="ps-peer-detail" title={a.detail}>
                  {a.detail.slice(0, 50)}
                </span>
                <span class="ps-peer-ts">
                  {new Date(a.ts).toLocaleTimeString("fr-FR", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </span>
              </div>
            )}
          </For>
        </div>
      </Show>
      <Show when={sessions().length === 0}>
        <div class="ps-empty-state">
          <p>Aucune session PowerShell ouverte.</p>
          <button class="btn btn--sm" onClick={pickOrOpenSession} disabled={opening()}>
            {opening() ? "Ouverture…" : "Ouvrir une session"}
          </button>
        </div>
      </Show>
      <Show when={showSessionPicker()}>
        <div class="ps-session-picker-backdrop">
          <div class="ps-session-picker">
            <div class="ps-session-picker-header">
              <h3>Sessions ouvertes</h3>
              <button class="btn btn--xs btn--ghost" onClick={() => setShowSessionPicker(false)}>
                ✕
              </button>
            </div>
            <div class="ps-session-picker-list">
              <For each={sessions()}>
                {(s) => (
                  <button class="ps-session-picker-item" onClick={() => selectExistingSession(s.psSessionId)}>
                    <span class="ps-session-picker-context">{psContextLabel(s.context)}</span>
                    <span class="ps-session-picker-name">{s.name || s.psSessionId.slice(0, 8)}</span>
                    <Show when={s.runAs?.credentialRef}>
                      {(ref) => <span class="ps-session-picker-cred">{shortCredentialRef(ref())}</span>}
                    </Show>
                  </button>
                )}
              </For>
            </div>
            <div class="ps-session-picker-footer">
              <button
                class="btn btn--sm btn--primary"
                onClick={() => {
                  setShowSessionPicker(false);
                  void openSession();
                }}
                disabled={opening()}
              >
                + Nouvelle session
              </button>
            </div>
          </div>
        </div>
      </Show>
      <Show when={splitMode() && activeId()}>
        {(id) => (
          <SplitView
            topId={id()}
            client={client}
            insertCmd={insertCmd}
            onInsertConsumed={onInsertConsumed}
            context={activeContext()}
            runAs={activeRunAs()}
            confirm={confirm.confirm}
          />
        )}
      </Show>
    </PanelLayout>
  );
};

registerPanel("powershell", PowerShellPanel);

export default PowerShellPanel;
